import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getUnixTime } from '@einhaus/meteoric-helpers';
import { trpc } from '@/api/trpc';

const money = (value: number | string) => Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const eastern = (timestamp: number) => new Date(timestamp * 1000).toLocaleString('en-US', { timeZone: 'America/New_York' });

export default function FuturesPaperPilot({
    portfolios,
    contracts
}: {
    portfolios: { id: number; name: string }[];
    contracts: { id: number; canonicalSymbol: string }[];
}) {
    const [form, setForm] = useState({
        portfolioId: '',
        contractId: '',
        strategyId: '',
        entryLimit: '',
        closeLimit: '',
        start: '',
        end: ''
    });
    const [error, setError] = useState('');
    const [ack, setAck] = useState(false);
    const preview = useMutation(trpc.admin.previewFuturesPaperPilot.mutationOptions({ retry: false }));
    const create = useMutation(trpc.admin.createFuturesPaperPilot.mutationOptions({ retry: false }));
    const activation = useMutation(trpc.admin.reviewFuturesPaperPilotActivation.mutationOptions({ retry: false }));
    const arm = useMutation(trpc.admin.armFuturesPaperPilot.mutationOptions({ retry: false }));
    const stop = useMutation(trpc.admin.stopFuturesPaperPilot.mutationOptions({ retry: false }));
    const pilots = useQuery(trpc.admin.listFuturesPaperPilots.queryOptions(undefined, { refetchInterval: 5000 }));
    const [review, setReview] = useState<{
        request: Parameters<typeof preview.mutateAsync>[0];
        result: Awaited<ReturnType<typeof preview.mutateAsync>>;
    } | null>(null);
    const [armReview, setArmReview] = useState<Awaited<ReturnType<typeof activation.mutateAsync>> | null>(null);
    const busy = preview.isPending || create.isPending || activation.isPending || arm.isPending || stop.isPending;
    const update = (key: keyof typeof form, value: string) => {
        setForm((old) => ({ ...old, [key]: value }));
        setReview(null);
        setArmReview(null);
        setAck(false);
        setError('');
    };
    const report = (cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause));
    return (
        <section className="space-y-4 rounded border border-slate-700 p-5">
            <h2 className="text-xl font-semibold">Bounded automated MES paper pilot</h2>
            <p>
                One completed five-minute SPY signal from an existing active, unscored long strategy can open one dated MES contract. The
                cash allocation stays $5,000 and the margin plus estimated entry commission ceiling stays $3,500. One opening attempt, a
                maximum thirty-minute entry window, and five-minute protection. Saving a draft does not activate it.
            </p>
            {(error || pilots.error) && (
                <p role="alert" className="text-red-300">
                    {error || pilots.error?.message}
                </p>
            )}
            <form
                className="grid gap-3 sm:grid-cols-2"
                onSubmit={async (event) => {
                    event.preventDefault();
                    setError('');
                    setReview(null);
                    try {
                        const request = {
                            portfolioId: Number(form.portfolioId),
                            contractId: Number(form.contractId),
                            strategyId: Number(form.strategyId),
                            entryLimit: Number(form.entryLimit),
                            closeLimit: Number(form.closeLimit),
                            capitalCeiling: 3500 as const,
                            startsTimestamp: getUnixTime(form.start.replace('T', ' ') + ':00'),
                            endsTimestamp: getUnixTime(form.end.replace('T', ' ') + ':00')
                        };
                        setReview({ request, result: await preview.mutateAsync(request) });
                    } catch (cause) {
                        report(cause);
                    }
                }}
            >
                <label>
                    Dedicated paper portfolio
                    <select
                        required
                        value={form.portfolioId}
                        onChange={(e) => update('portfolioId', e.target.value)}
                        className="block w-full bg-slate-900 p-2"
                    >
                        <option value="">Choose portfolio</option>
                        {portfolios.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.name}
                            </option>
                        ))}
                    </select>
                </label>
                <label>
                    Dated MES contract
                    <select
                        required
                        value={form.contractId}
                        onChange={(e) => update('contractId', e.target.value)}
                        className="block w-full bg-slate-900 p-2"
                    >
                        <option value="">Choose contract</option>
                        {contracts.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.canonicalSymbol}
                            </option>
                        ))}
                    </select>
                </label>
                <label>
                    Existing SPY signal strategy ID
                    <input
                        required
                        type="number"
                        min="1"
                        value={form.strategyId}
                        onChange={(e) => update('strategyId', e.target.value)}
                        className="block w-full bg-slate-900 p-2"
                    />
                </label>
                <label>
                    MES entry buy cap
                    <input
                        required
                        type="number"
                        min="0.25"
                        step="0.25"
                        value={form.entryLimit}
                        onChange={(e) => update('entryLimit', e.target.value)}
                        className="block w-full bg-slate-900 p-2"
                    />
                </label>
                <label>
                    Protective MES sell floor
                    <input
                        required
                        type="number"
                        min="0.25"
                        step="0.25"
                        value={form.closeLimit}
                        onChange={(e) => update('closeLimit', e.target.value)}
                        className="block w-full bg-slate-900 p-2"
                    />
                </label>
                <label>
                    Entry window starts (Eastern)
                    <input
                        required
                        type="datetime-local"
                        value={form.start}
                        onChange={(e) => update('start', e.target.value)}
                        className="block w-full bg-slate-900 p-2"
                    />
                </label>
                <label>
                    Entry window ends (Eastern)
                    <input
                        required
                        type="datetime-local"
                        value={form.end}
                        onChange={(e) => update('end', e.target.value)}
                        className="block w-full bg-slate-900 p-2"
                    />
                </label>
                <button disabled={busy} className="rounded bg-blue-800 p-2 disabled:opacity-40">
                    Preview inactive draft
                </button>
            </form>
            {review && (
                <div className="space-y-2 rounded border border-slate-600 p-3">
                    <p>
                        Strategy {review.result.strategyId} · one MES contract · {eastern(review.result.startsTimestamp)}–
                        {eastern(review.result.endsTimestamp)} Eastern
                    </p>
                    <p>
                        Buy cap {review.result.entryLimit}; sell floor {review.result.closeLimit}. Native margin and fresh data are checked
                        again before arming and before entry.
                    </p>
                    <button
                        disabled={busy}
                        className="rounded bg-blue-800 p-2 disabled:opacity-40"
                        onClick={async () => {
                            try {
                                await create.mutateAsync({ ...review.request, expectedConfigurationKey: review.result.configurationKey });
                                setReview(null);
                                await pilots.refetch();
                            } catch (cause) {
                                report(cause);
                            }
                        }}
                    >
                        Save inactive draft
                    </button>
                </div>
            )}
            {pilots.data?.map((p) => (
                <article key={p.id} className="space-y-2 border-t border-slate-700 pt-3">
                    <p>
                        Pilot {p.id} · {p.status} · strategy {p.strategyId} · {eastern(p.startsTimestamp)}–{eastern(p.endsTimestamp)}{' '}
                        Eastern
                    </p>
                    {p.lastError && (
                        <p role="alert" className="text-amber-300">
                            {p.lastError}
                        </p>
                    )}
                    {p.positionId !== null && (
                        <a className="underline" href={`/tools/futuresPaper?positionId=${p.positionId}`}>
                            Inspect native position {p.positionId}
                        </a>
                    )}
                    {p.status === 'draft' && (
                        <button
                            disabled={busy}
                            className="mr-3 underline disabled:opacity-40"
                            onClick={async () => {
                                setArmReview(null);
                                setAck(false);
                                setError('');
                                try {
                                    setArmReview(await activation.mutateAsync({ pilotId: p.id }));
                                } catch (cause) {
                                    report(cause);
                                }
                            }}
                        >
                            Review activation
                        </button>
                    )}
                    {['draft', 'armed', 'signaled', 'submitted'].includes(p.status) && (
                        <button
                            disabled={busy}
                            className="underline disabled:opacity-40"
                            onClick={async () => {
                                try {
                                    await stop.mutateAsync({ pilotId: p.id });
                                    setArmReview(null);
                                    setAck(false);
                                    await pilots.refetch();
                                } catch (cause) {
                                    report(cause);
                                }
                            }}
                        >
                            Stop new entries
                        </button>
                    )}
                </article>
            ))}
            {armReview && (
                <div className="space-y-3 rounded border border-amber-600 p-4">
                    <h3 className="font-semibold">Activate pilot {armReview.pilot.id}</h3>
                    <p>
                        {armReview.preview.brokerSymbol} · one long contract · last trade {armReview.preview.lastTradePrice} at{' '}
                        {eastern(armReview.preview.sourceTradeTimestamp)} Eastern. Initial margin {money(armReview.preview.initialMargin)};
                        estimated entry commission {money(armReview.preview.estimatedCommission)}. Buy cap {armReview.pilot.entryLimit};
                        sell floor {armReview.pilot.closeLimit}.
                    </p>
                    <p>
                        Activation permits one automatic paper opening in this window. Authored exits use the reviewed limit; deadline
                        protection uses a SIM market close without a price floor, even during a feed outage. A halt can delay fills. An
                        uncertain order keeps the account reserved. Stopping entries keeps protection running. The pilot never rearms
                        itself.
                    </p>
                    <label className="block">
                        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> I approve this one automated
                        paper attempt and its reviewed limits.
                    </label>
                    <button
                        disabled={!ack || busy}
                        className="rounded bg-amber-800 p-2 disabled:opacity-40"
                        onClick={async () => {
                            try {
                                await arm.mutateAsync({
                                    pilotId: armReview.pilot.id,
                                    expectedConfigurationKey: armReview.pilot.configurationKey,
                                    expectedInstrumentKey: armReview.preview.instrumentKey,
                                    acknowledgeOneAutomatedPaperAttempt: true
                                });
                                setArmReview(null);
                                setAck(false);
                                await pilots.refetch();
                            } catch (cause) {
                                report(cause);
                                setArmReview(null);
                                setAck(false);
                            }
                        }}
                    >
                        Activate this paper pilot
                    </button>
                </div>
            )}
        </section>
    );
}
